import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { SCHLUESSEL_GRUND } from '../../utils/inaktivitaet';
import { Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { MeshGradientBackground } from './login-components/MeshGradientBackground';
import { ParticleBackground } from './login-components/ParticleBackground';
import { CleanInput } from './login-components/CleanInput';
import './login-components/login-page-clean.css';
import '../../styles/modern-design.css';

/** Hinweis nach automatischer Abmeldung (einmalig, nur dieser Tab). */
function abmeldeHinweis(): string {
  try {
    const grund = window.sessionStorage.getItem(SCHLUESSEL_GRUND);
    window.sessionStorage.removeItem(SCHLUESSEL_GRUND);
    return grund === 'inaktiv' ? 'Sie wurden nach 5 Minuten ohne Aktivität abgemeldet. Bitte melden Sie sich erneut an.' : '';
  } catch {
    return '';
  }
}

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hinweis] = useState(abmeldeHinweis);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login({ email, password });
      // Success animation before navigation
      await new Promise(resolve => setTimeout(resolve, 600));
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.error || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = (e: React.MouseEvent) => {
    e.preventDefault();
    // Kein Self-Service-Reset verfügbar (Custom-Auth). Ehrliche Rückmeldung
    // statt stillem Nichtstun, damit ausgesperrte Nutzer wissen, was zu tun ist.
    alert('Passwort zurücksetzen ist aktuell nur über den Administrator möglich. Bitte wenden Sie sich an Ihren Administrator.');
  };

  return (
    <div className="login-page-container">
      {/* Background Effects */}
      <MeshGradientBackground />
      <ParticleBackground />

      {/* Main Content */}
      <motion.div
        className="login-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Logo */}
        <motion.div
          className="login-logo"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 20 }}
        >
          <div className="logo-icon">HR</div>
        </motion.div>

        {/* Login Card */}
        <motion.div
          className="login-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {/* Header */}
          <div className="card-header">
            <h1 className="card-title">Willkommen zurück</h1>
            <p className="card-subtitle">Melden Sie sich in Ihrem Konto an</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <CleanInput
              type="email"
              value={email}
              onChange={setEmail}
              label="E-Mail-Adresse"
              placeholder="name@firma.de"
              icon="email"
              error={error && !email ? 'E-Mail ist erforderlich' : undefined}
            />

            <CleanInput
              type="password"
              value={password}
              onChange={setPassword}
              label="Passwort"
              placeholder="Geben Sie Ihr Passwort ein"
              icon="password"
              error={error && !password ? 'Passwort ist erforderlich' : undefined}
            />

            {hinweis && !error && (
              <div className="error-banner" role="status">
                <AlertCircle size={18} />
                <span>{hinweis}</span>
              </div>
            )}

            {/* Error Banner */}
            <AnimatePresence>
              {error && (
                <motion.div
                  className="error-banner"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </motion.div>
              )}
              </AnimatePresence>

            {/* Options */}
            <div className="form-options">
              <button type="button" onClick={handlePasswordReset} className="forgot-link">
                Passwort vergessen?
              </button>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              className="submit-btn"
              disabled={isLoading}
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="spinner" size={20} />
                  <span>Anmeldung...</span>
                </>
              ) : (
                <>
                  <span>Anmelden</span>
                  <ArrowRight size={20} />
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="login-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p>© 2025 OctaAI. Alle Rechte vorbehalten.</p>
        </motion.div>
      </motion.div>
    </div>
  );
};
